/**
 * End game / new table. SPEC.md §32.1, §32.2.
 *
 * Two destructive-looking doors, and only one of them is actually destructive.
 * Ending makes this table read-only for everyone (§32.1). "New table" is §32.2's
 * preferred reset: a fresh session, so nothing that already happened is deleted or
 * overwritten to get a clean scoreboard.
 *
 * Raised by the host menu that owns it, so its open state is a prop rather than
 * another ui-store flag — nothing else can open it. §37
 */

import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Sheet } from '@/components/ui/Sheet';
import { useSession } from '@/hooks/useSession';
import { createSession, errorMessageOf } from '@/lib/api';
import { writePlayerToken } from '@/lib/storage';
import { navigate, paths } from '@/router';
import { useMe } from '@/stores/session-store';

export interface EndGameConfirmProps {
  open: boolean;
  onClose(): void;
}

export function EndGameConfirm({ open, onClose }: EndGameConfirmProps) {
  const me = useMe();
  const { endGame, interactive } = useSession();

  const [pending, setPending] = useState<'end' | 'new' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const end = async (): Promise<void> => {
    setPending('end');
    setError(null);
    const ok = await endGame();
    setPending(null);
    // On success the session broadcast flips everyone to the final score screen;
    // a failure has already surfaced its shared copy. §32.1, §35
    if (ok) onClose();
  };

  const startNewTable = async (): Promise<void> => {
    if (!me) return;
    setPending('new');
    setError(null);
    try {
      // Reuses this device's identity, so a fresh table is one tap rather than
      // another join form. §6.3, §32.2
      const credentials = await createSession({ name: me.name, avatarKey: me.avatarKey });
      writePlayerToken(credentials.sessionId, credentials.playerToken);
      navigate(paths.game(credentials.sessionId));
    } catch (err) {
      setError(errorMessageOf(err));
      setPending(null);
    }
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="End this game?"
      description="Everyone sees the final scores, and the table stops taking points."
      footer={
        <>
          {error === null ? null : (
            <p role="alert" className="text-center text-sm text-negative">
              {error}
            </p>
          )}
          <Button
            variant="danger"
            size="lg"
            fullWidth
            loading={pending === 'end'}
            disabled={!interactive || pending === 'new'}
            onClick={() => {
              void end();
            }}
          >
            End Game
          </Button>
          <Button variant="ghost" fullWidth onClick={onClose}>
            Keep playing
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4 pb-2">
        <p className="text-sm text-ink-muted">
          Nothing is deleted. Every hand, every point change and the final score stay readable in
          the history — the table just becomes read-only.
        </p>

        <div className="flex flex-col gap-2 rounded-card border border-line bg-raised px-3 py-3">
          <p className="text-sm font-semibold text-ink">Just want a clean scoreboard?</p>
          <p className="text-sm text-ink-muted">
            Start a new table instead. This game keeps its history exactly as it is, and you get a
            fresh code to share.
          </p>
          <Button
            variant="secondary"
            fullWidth
            loading={pending === 'new'}
            disabled={!me || pending === 'end'}
            onClick={() => {
              void startNewTable();
            }}
          >
            Start New Game
          </Button>
        </div>
      </div>
    </Sheet>
  );
}
